// Phase HR-003 Wave 4 — Worker Efficiency capture (Doc §26).
//
// Operator flow (~45 sec per shift per side):
//   1. Supervisor picks date + shift + department.
//   2. Page auto-fills a grid of allocated workers (from worker_allocations).
//   3. For each worker: types standard kg + actual kg. Efficiency % computes live.
//   4. If efficiency < 85% → reason chip strip auto-opens.
//   5. Tap "Save shift" → one bulk POST.
//
// Threshold colors (doc §6):  ≥90 green  /  85-89 amber  /  <85 red.

import { useEffect, useMemo, useState } from 'react'
import {
  Alert, App, Button, DatePicker, Empty, Input, InputNumber, Select, Space, Tag, Typography,
} from 'antd'
import { ReloadOutlined, SaveOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs, { type Dayjs } from 'dayjs'
import {
  workerEfficiencyApi,
  type EligibleWorker, type LossReasonCode, type WorkerOutputRow,
} from '@/features/hr/workerEfficiencyApi'
import { departmentsApi } from '@/features/masters/departments/api'
import apiClient from '@/shared/api/client'
import { filterActiveShifts } from '@/shared/constants/shifts'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'
import { usePlantId } from '@/shared/hooks/usePlantId'
import {
  efficiencyPctClass, useHrMobilePolish, type ThresholdResult,
} from '@/shared/styles/hrMobilePolish'
import type { Shift } from '@/shared/types/master'

const { Text } = Typography

const LOSS_REASONS: { code: LossReasonCode; label: string }[] = [
  { code: 'EXCESS_BREAKAGE',     label: 'Breakage' },
  { code: 'SHORTAGE_OF_HELPER',  label: 'Helper short' },
  { code: 'MATERIAL_DELAY',      label: 'Material' },
  { code: 'MACHINE_DOWNTIME',    label: 'Downtime' },
  { code: 'QUALITY_REJECT',      label: 'Quality' },
  { code: 'SUPERVISOR_INSTRUCTION', label: 'Sup instr' },
  { code: 'TRAINING_REFRESHER_NEEDED', label: 'Refresher' },
  { code: 'OTHER',               label: 'Other…' },
]

interface Draft {
  standard: number | null
  actual: number | null
  downtime: number | null
  reasonCode: LossReasonCode | null
  reasonText: string
  action: string
  dirty: boolean
}

function blankDraft(w: EligibleWorker): Draft {
  return {
    standard: w.existing_standard_kg ?? null,
    actual: w.existing_actual_kg ?? null,
    downtime: w.existing_downtime_minutes ?? null,
    reasonCode: w.existing_loss_reason_code ?? null,
    reasonText: '',
    action: w.existing_action_taken ?? '',
    dirty: false,
  }
}

export default function WorkerEfficiencyPage() {
  useHrMobilePolish()
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { plantId } = usePlantId()
  const canSave = usePermission('attendance.create')

  const [date, setDate] = useState<Dayjs>(dayjs())
  const [deptId, setDeptId] = useState<string | undefined>()
  const [shiftId, setShiftId] = useState<string | undefined>()
  const dateStr = date.format('YYYY-MM-DD')

  const { data: deptsData } = useQuery({
    queryKey: ['we-depts', plantId],
    queryFn: () => departmentsApi.list({ page: 1, page_size: 500, plant_id: plantId }),
    enabled: !!plantId,
  })
  const { data: shiftsData } = useQuery({
    queryKey: ['we-shifts', plantId],
    queryFn: async () => {
      const r = await apiClient.get<Shift[] | { items: Shift[] }>('/shifts',
        { params: plantId ? { plant_id: plantId } : {} })
      const raw = Array.isArray(r.data) ? r.data : (r.data as { items: Shift[] }).items ?? []
      return filterActiveShifts(raw)
    },
    enabled: !!plantId,
  })

  const ready = !!plantId && !!shiftId && !!deptId
  const { data: eligible = [], isFetching, refetch } = useQuery({
    queryKey: ['we-eligible', plantId, dateStr, shiftId, deptId],
    queryFn: () => workerEfficiencyApi.eligible({
      output_date: dateStr, shift_id: shiftId!, department_id: deptId!,
    }),
    enabled: ready,
  })

  // ── Working drafts per employee_id ─────────────────────────────
  const [drafts, setDrafts] = useState<Record<string, Draft>>({})

  // Seed/refresh drafts when the eligible list changes
  useEffect(() => {
    const next: Record<string, Draft> = {}
    for (const w of eligible) next[w.employee_id] = blankDraft(w)
    setDrafts(next)
  }, [eligible])

  const setField = (empId: string, patch: Partial<Draft>) => {
    setDrafts((prev) => ({
      ...prev,
      [empId]: { ...prev[empId], ...patch, dirty: true },
    }))
  }

  // KPI tiles
  const stats = useMemo(() => {
    let total = 0; let totalEff = 0; let below85 = 0
    let totalTarget = 0; let totalActual = 0
    for (const w of eligible) {
      const d = drafts[w.employee_id]
      if (!d || d.standard == null || d.actual == null || !d.standard) continue
      total += 1
      const eff = (d.actual / d.standard) * 100
      totalEff += eff
      totalTarget += d.standard
      totalActual += d.actual
      if (eff < 85) below85 += 1
    }
    const avg = total ? totalEff / total : null
    return { total, avg, below85, totalTarget, totalActual,
             dirty: Object.values(drafts).filter(d => d.dirty).length }
  }, [eligible, drafts])

  // ── Bulk save ─────────────────────────────────────────────────
  const saveMut = useMutation({
    mutationFn: async () => {
      if (!plantId || !shiftId || !deptId) {
        throw new Error('Pick date + shift + department first.')
      }
      const rows: WorkerOutputRow[] = []
      for (const w of eligible) {
        const d = drafts[w.employee_id]
        if (!d || !d.dirty) continue
        if (d.standard == null || d.actual == null) continue  // skip incomplete
        rows.push({
          employee_id: w.employee_id,
          machine_id: w.machine_id,
          section_code: w.section_code,
          allocation_id: w.allocation_id,
          standard_target_kg: d.standard,
          actual_output_kg: d.actual,
          downtime_minutes: d.downtime ?? 0,
          loss_reason_code: d.reasonCode,
          loss_reason_text: d.reasonCode === 'OTHER' ? d.reasonText : null,
          action_taken: d.action || null,
        })
      }
      if (rows.length === 0) throw new Error('Nothing to save.')
      return workerEfficiencyApi.bulkSave({
        output_date: dateStr, shift_id: shiftId, plant_id: plantId,
        department_id: deptId, rows,
      })
    },
    onSuccess: (saved) => {
      message.success(`Saved ${saved.length} worker outputs.`)
      qc.invalidateQueries({ queryKey: ['we-eligible'] })
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) => {
      message.error(e?.message ?? e?.response?.data?.detail ?? 'Save failed')
    },
  })

  return (
    <div className="hr-mobile-root">
      <PageHeader
        title="Worker Efficiency"
        subtitle="Doc §26 — per-worker output vs standard. Tap-fill at shift end."
      />

      {/* Filters */}
      <Space size={6} wrap style={{ marginBottom: 12 }}>
        <DatePicker value={date} allowClear={false}
          onChange={(d) => d && setDate(d)} />
        <Select allowClear placeholder="Shift" style={{ minWidth: 160 }}
          value={shiftId} onChange={setShiftId}
          options={(shiftsData ?? []).map((s) => ({
            label: `${s.shift_name} (${s.shift_code})`, value: s.id,
          }))} />
        <Select allowClear placeholder="Department" style={{ minWidth: 180 }}
          value={deptId} onChange={setDeptId}
          options={(deptsData?.items ?? []).map((d) => ({ label: d.name, value: d.id }))} />
        <Button icon={<ReloadOutlined />} onClick={() => refetch()} />
      </Space>

      {/* KPI tiles */}
      <div style={{
        display: 'grid', gap: 8, marginBottom: 12,
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
      }}>
        <KPITile
          variant={stats.avg == null ? 'neutral' : efficiencyPctClass(stats.avg)}
          value={stats.avg == null ? '—' : `${stats.avg.toFixed(1)}%`}
          label="Avg efficiency" />
        <KPITile
          variant={stats.below85 === 0 ? 'green' : stats.below85 <= 2 ? 'amber' : 'red'}
          value={String(stats.below85)}
          label="Below 85%" />
        <KPITile variant="neutral"
          value={`${stats.totalActual.toFixed(0)} / ${stats.totalTarget.toFixed(0)}`}
          label="Actual / target (kg)" />
        <KPITile variant="neutral" value={String(stats.total)} label="Captured workers" />
      </div>

      {!ready && (
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message="Pick a date, shift, and department to load the worker grid." />
      )}

      {ready && isFetching && (
        <Empty description="Loading eligible workers…" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      )}

      {ready && !isFetching && eligible.length === 0 && (
        <Empty description="No allocated workers for this date+shift+department. Allocate first." />
      )}

      {ready && eligible.length > 0 && (
        <div style={{
          display: 'grid', gap: 8,
          gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
        }}>
          {eligible.map((w) => (
            <WorkerCard key={w.employee_id} w={w}
                        draft={drafts[w.employee_id]}
                        onPatch={(p) => setField(w.employee_id, p)} />
          ))}
        </div>
      )}

      {canSave && stats.dirty > 0 && (
        <div className="hr-sticky-actions">
          <Button type="primary" size="large" icon={<SaveOutlined />}
            loading={saveMut.isPending}
            onClick={() => saveMut.mutate()}>
            Save {stats.dirty} change{stats.dirty > 1 ? 's' : ''}
          </Button>
        </div>
      )}
    </div>
  )
}

// ─── Sub-components ────────────────────────────────────────────────

function KPITile({ variant, value, label }: {
  variant: ThresholdResult; value: number | string; label: string
}) {
  return (
    <div className={`threshold-tile threshold-tile--${variant}`}>
      <div className="threshold-tile__value">{value}</div>
      <div className="threshold-tile__label">{label}</div>
    </div>
  )
}

function WorkerCard({ w, draft, onPatch }: {
  w: EligibleWorker
  draft: Draft | undefined
  onPatch: (patch: Partial<Draft>) => void
}) {
  const d = draft ?? blankDraft(w)
  const eff = (d.standard != null && d.actual != null && d.standard)
    ? (d.actual / d.standard) * 100
    : null
  const effClass = eff == null ? 'neutral' : efficiencyPctClass(eff)
  const showReason = eff != null && eff < 85
  const stripe =
    effClass === 'red'   ? '#cf1322' :
    effClass === 'amber' ? '#d48806' :
    effClass === 'green' ? '#389e0d' :
    undefined

  return (
    <div className={'hr-roster-card' + (d.dirty ? ' hr-roster-card--dirty' : '')}
         style={{ flexDirection: 'column', alignItems: 'stretch',
                  borderLeft: stripe ? `4px solid ${stripe}` : undefined }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Text strong>{w.emp_name}</Text>
        <Text type="secondary" style={{ fontFamily: 'monospace', fontSize: 11 }}>
          {w.emp_code}
        </Text>
        {w.machine_code && <Tag>{w.machine_code}</Tag>}
        {w.section_code && <Tag color="purple">{w.section_code}</Tag>}
        <Tag color="blue">{w.required_role}</Tag>
        {eff != null && (
          <Tag color={effClass === 'red' ? 'red' : effClass === 'amber' ? 'gold'
                     : effClass === 'green' ? 'green' : 'default'}>
            {eff.toFixed(1)}%
          </Tag>
        )}
      </div>

      <div style={{
        display: 'grid', gap: 8, marginTop: 10,
        gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
      }}>
        <NumField label="Standard (kg)" value={d.standard}
                  onChange={(v) => onPatch({ standard: v })} />
        <NumField label="Actual (kg)" value={d.actual}
                  onChange={(v) => onPatch({ actual: v })} />
        <NumField label="Downtime (min)" value={d.downtime}
                  onChange={(v) => onPatch({ downtime: v })} />
      </div>

      {showReason && (
        <div style={{ marginTop: 8 }}>
          <Text type="secondary" style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.5 }}>
            LOSS REASON:
          </Text>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
            {LOSS_REASONS.map((r) => {
              const sel = d.reasonCode === r.code
              return (
                <button
                  key={r.code} type="button"
                  className={`hr-filter-chip${sel ? ' hr-filter-chip--active' : ''}`}
                  style={{ height: 28, padding: '0 10px', fontSize: 11 }}
                  onClick={() => onPatch({ reasonCode: sel ? null : r.code })}
                >{r.label}</button>
              )
            })}
          </div>
          {d.reasonCode === 'OTHER' && (
            <Input size="small" placeholder="Specify reason"
                   value={d.reasonText} style={{ marginTop: 6, width: 220 }}
                   onChange={(e) => onPatch({ reasonText: e.target.value })} />
          )}
          <Input size="small" placeholder="Action / coaching note (optional)"
                 value={d.action} style={{ marginTop: 6 }}
                 onChange={(e) => onPatch({ action: e.target.value })} />
        </div>
      )}
    </div>
  )
}

function NumField({ label, value, onChange }: {
  label: string; value: number | null; onChange: (v: number | null) => void
}) {
  return (
    <div>
      <Text type="secondary" style={{ fontSize: 10, fontWeight: 600,
                                       letterSpacing: 0.5, display: 'block' }}>
        {label.toUpperCase()}
      </Text>
      <InputNumber
        value={value} onChange={onChange}
        style={{ width: '100%' }}
        controls={false}
        inputMode="decimal"
        min={0} step={0.1}
      />
    </div>
  )
}
